import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { PageHeader } from "../../components/dash/ui";
import { Deadline, ReqBadge, ReqHistory, wibTime } from "../../components/ManagerRequests";
import { api, formatApiError } from "../../lib/api";
import { HandoverList } from "../../components/Handover";
import { ProofLink } from "../../components/Bookkeeping";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { ROLE_HOME } from "../../lib/labels";

const RequestCard = ({ r, reload }) => {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const respond = async (accept) => {
    if (!window.confirm(accept ? "Terima pengajuan ini? PT SJI akan meninjau sebelum resmi ditetapkan." : "Tolak pengajuan ini?")) return;
    setBusy(true);
    try { await api.post(`/manager-requests/${r.id}/respond`, { accept, note }); toast.success(accept ? "Diterima — menunggu persetujuan PT SJI" : "Pengajuan ditolak"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); reload(); }
  };
  const waiting = r.status === "MENUNGGU_PENGELOLA";
  return (
    <li className={`rounded-xl border p-4 text-sm ${waiting ? "border-amber-300 bg-amber-50" : "bg-white"}`} data-testid={`inbox-req-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0"><p className="font-bold text-slate-900 break-words">{r.projectCode} · {r.projectName}</p>
          <p className="text-xs text-slate-600">{r.projectHectares != null ? `${r.projectHectares} ha · ` : ""}diajukan oleh {r.requestedByName} ({r.requestedByRole === "SUPER_ADMIN" ? "PT SJI" : "Investor"}) · {wibTime(r.createdAt)}</p></div>
        <ReqBadge r={r} testid={`inbox-req-status-${r.id}`} />
      </div>
      <Deadline r={r} />
      {waiting && (
        <div className="mt-3 space-y-2">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan (wajib bila menolak)" data-testid={`inbox-req-note-${r.id}`} />
          <div className="flex flex-wrap gap-2">
            <Button size="sm" className="bg-emerald-800 hover:bg-emerald-900" disabled={busy} onClick={() => respond(true)} data-testid={`inbox-req-accept-${r.id}`}>Terima</Button>
            <Button size="sm" variant="outline" className="text-red-700" disabled={busy || note.trim().length < 3} onClick={() => respond(false)} data-testid={`inbox-req-decline-${r.id}`}>Tolak</Button>
          </div>
        </div>)}
      <ReqHistory r={r} />
    </li>
  );
};

export default function ManagerRequestsInbox() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    api.get("/manager-requests/inbox").then((r) => {
      setData(r.data);
      if (r.data.unread) { api.post("/manager-requests/inbox/read-all").then(() => window.dispatchEvent(new Event("manager-inbox-updated"))); }
      else window.dispatchEvent(new Event("manager-inbox-updated"));
    }).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-5" data-testid="manager-inbox-page">
      <PageHeader title="Permintaan Pengelolaan" subtitle="Investor atau PT SJI mengajukan Anda sebagai pengelola kebun. Terima atau tolak; bila Anda terima, PT SJI meninjau sebelum resmi ditetapkan." />
      {!data ? <Loader2 className="h-6 w-6 animate-spin text-emerald-700" /> : (
        <>
          <HandoverList url="/handovers/mine" title="Serah Terima Kebun" testid="inbox-handovers" />
          {data.notifications.length > 0 && (
            <ul className="rounded-xl border bg-white p-4 space-y-1 text-xs" data-testid="inbox-notifications">
              <li className="text-sm font-bold text-slate-800 mb-1">Notifikasi</li>
              {data.notifications.slice(0, 10).map((n) => <li key={n.id} className={n.read ? "text-slate-500" : "font-semibold text-slate-900"} data-testid={`inbox-notif-${n.id}`}>{wibTime(n.at)} · {n.text}{n.contractId && <> · <Link to={`${ROLE_HOME[user.role]}/bagi-hasil/${n.contractId}?tab=laporan`} className="text-emerald-800 underline" data-testid={`inbox-notif-open-${n.id}`}>Buka Bagi Hasil</Link></>} <ProofLink n={n} testid={`inbox-notif-proof-${n.id}`} /></li>)}
            </ul>)}
          {!data.items.length ? <p className="text-sm text-slate-500" data-testid="inbox-empty">Belum ada permintaan pengelolaan.</p>
            : <ul className="space-y-3" data-testid="inbox-list">{data.items.map((r) => <RequestCard key={r.id} r={r} reload={load} />)}</ul>}
        </>)}
    </div>
  );
}
